import { TablePropertyHandler, ColumnPropertyHandler, ColumnPropertyFlag } from "./components/property-handlers";
import {
    UserProfileDescription,
    TypeSchema,
    TableGlideType,
    isPrimitiveType,
    getTableName,
    findTable,
    makeInputOutputTables,
    getPrimitiveNonHiddenColumns,
    TableColumn,
    isUserAgnosticDataColumn,
} from "./description";
import { PropertySection } from "./components/property-sections";
import { canBeOwnersColumn, findFittingColumnInTable } from "./description-utils";
import { makeTableProperty, makeColumnProperty, getTableProperty } from "./components/property-utils";
import { PropertyDescriptor, RewritingComponentConfiguratorContext } from "./components";
import { populateDescription } from "./components/populate-description";
import { PopulationMode } from "./components/description-handlers";
import { EminenceFlags } from "../Database/eminence";
import { getFeatureSetting } from "../feature-settings";

export function makeUserProfileDataForTable(table: TableGlideType): UserProfileDescription | undefined {
    const emailColumn = findFittingColumnInTable(table, ["email"], undefined, "email-address", isPrimitiveType);
    if (emailColumn === undefined) return undefined;
    const excludeNames = new Set([emailColumn.name]);

    const nameColumn = findFittingColumnInTable(table, ["name"], excludeNames, "string", isPrimitiveType);
    if (nameColumn === undefined) return undefined;
    excludeNames.add(nameColumn.name);

    const imageColumn = findFittingColumnInTable(
        table,
        ["image", "photo", "picture", "pic"],
        excludeNames,
        "image-uri",
        isPrimitiveType
    );
    if (imageColumn === undefined) return undefined;

    return {
        userProfileTable: makeTableProperty(getTableName(table)),
        emailColumn: makeColumnProperty(emailColumn.name),
        nameColumn: makeColumnProperty(nameColumn.name),
        imageColumn: makeColumnProperty(imageColumn.name),
        rolesColumn: undefined,
    };
}

function getAllowedTables(_desc: UserProfileDescription, schema: TypeSchema): readonly TableGlideType[] {
    return schema.tables.filter(t => makeUserProfileDataForTable(t) !== undefined);
}

function getPrimitiveNonHiddenUserAgnosticDataColumns(t: TableGlideType): readonly TableColumn[] {
    return getPrimitiveNonHiddenColumns(t).filter(isUserAgnosticDataColumn);
}

const tablePropertyHandler = new TablePropertyHandler(
    "userProfileTable",
    "Sheet",
    false,
    getAllowedTables,
    PropertySection.Source
);
const namePropertyHandler = new ColumnPropertyHandler(
    "nameColumn",
    "Name",
    [ColumnPropertyFlag.Editable, ColumnPropertyFlag.Required],
    undefined,
    ["name"],
    getPrimitiveNonHiddenUserAgnosticDataColumns,
    "string",
    PropertySection.Data
);
const emailPropertyHandler = new ColumnPropertyHandler(
    "emailColumn",
    "Email",
    [ColumnPropertyFlag.Editable, ColumnPropertyFlag.Required, ColumnPropertyFlag.EditedInApp],
    undefined,
    ["email"],
    getPrimitiveNonHiddenUserAgnosticDataColumns,
    "email-address",
    PropertySection.Data
);
const imagePropertyHandler = new ColumnPropertyHandler(
    "imageColumn",
    "Image",
    [ColumnPropertyFlag.Editable, ColumnPropertyFlag.Required],
    undefined,
    ["image", "photo", "picture", "pic"],
    getPrimitiveNonHiddenUserAgnosticDataColumns,
    "image-uri",
    PropertySection.Data
);
const rolePropertyHandler = new ColumnPropertyHandler(
    "rolesColumn",
    "Role",
    [ColumnPropertyFlag.Editable, ColumnPropertyFlag.Optional, ColumnPropertyFlag.EmptyByDefault],
    undefined,
    ["role", "permissions", "access"],
    t => t.columns.filter(canBeOwnersColumn),
    "string",
    PropertySection.Data
);

const userProfileDescriptorsWithoutRole = [
    tablePropertyHandler,
    namePropertyHandler,
    emailPropertyHandler,
    imagePropertyHandler,
];
const userProfileDescriptorsWithRole = [...userProfileDescriptorsWithoutRole, rolePropertyHandler];

export function getUserProfileDescriptors(flags: EminenceFlags): readonly PropertyDescriptor[] {
    const withRoles = flags.roles && getFeatureSetting("roles");
    if (withRoles) {
        return userProfileDescriptorsWithRole;
    } else {
        return userProfileDescriptorsWithoutRole;
    }
}

export function rewriteUserProfileDescription(
    desc: UserProfileDescription,
    ccc: RewritingComponentConfiguratorContext
): UserProfileDescription | undefined {
    const table = findTable(ccc.schema, getTableProperty(desc.userProfileTable));
    if (table === undefined) return undefined;

    return populateDescription(
        () => userProfileDescriptorsWithRole,
        PopulationMode.Rewrite,
        desc,
        desc,
        makeInputOutputTables(table),
        ccc,
        true,
        undefined
    );
}
